import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { CreateCaptureSessionRequest } from '../../interfaces/api/create-capture-session-request.interface';
import { CaptureSession } from '../../interfaces/state/entities/capture-session.interface';
import { ICaptureSessionsService } from '../../interfaces/services/capture-sessions-service.interface';
import { FloorsMockService } from '../floors/floors-mock.service';
import { mockDaysAgo } from '../mock/mock-data.utils';

@Injectable({ providedIn: 'root' })
export class CaptureSessionsMockService implements ICaptureSessionsService {
  private readonly floorsService = inject(FloorsMockService);
  private sessions: CaptureSession[] = [];
  private idCounter = 0;

  constructor() {
    const now = new Date();
    const mockSessions: CaptureSession[] = [
      {
        id: '1',
        floorId: '1',
        name: 'Insta360 X3 - iPhone 14 Pro',
        camera: 'Insta360 X3',
        device: 'iPhone 14 Pro',
        captureCount: 8,
        dateCreated: mockDaysAgo(now, 15),
        startedAt: mockDaysAgo(now, 15),
        drawingRevision: 2,
      },
      {
        id: '2',
        floorId: '1',
        name: 'GoPro Hero 11 - iPad Pro',
        camera: 'GoPro Hero 11',
        device: 'iPad Pro',
        captureCount: 4,
        dateCreated: mockDaysAgo(now, 8),
        startedAt: mockDaysAgo(now, 8),
        drawingRevision: 2,
      },
      {
        id: '3',
        floorId: '2',
        name: 'Insta360 One RS - Samsung Galaxy',
        camera: 'Insta360 One RS',
        device: 'Samsung Galaxy S23',
        captureCount: 0,
        dateCreated: mockDaysAgo(now, 5),
        startedAt: mockDaysAgo(now, 5),
        drawingRevision: 2,
      },
      {
        id: '4',
        floorId: '5',
        name: 'Ricoh Theta Z1 - iPhone 13',
        camera: 'Ricoh Theta Z1',
        device: 'iPhone 13',
        captureCount: 13,
        dateCreated: mockDaysAgo(now, 20),
        startedAt: mockDaysAgo(now, 20),
        drawingRevision: 1,
      },
      {
        id: '5',
        floorId: '6',
        name: 'Insta360 X3 - iPhone 15',
        camera: 'Insta360 X3',
        device: 'iPhone 15',
        captureCount: 8,
        dateCreated: mockDaysAgo(now, 14),
        startedAt: mockDaysAgo(now, 14),
        drawingRevision: 2,
      },
      {
        id: '6',
        floorId: '6',
        name: 'GoPro Max - Android Tablet',
        camera: 'GoPro Max',
        device: 'Android Tablet',
        captureCount: 2,
        dateCreated: mockDaysAgo(now, 7),
        startedAt: mockDaysAgo(now, 7),
        drawingRevision: 3,
      },
      {
        id: '7',
        floorId: '8',
        name: 'Insta360 Pro 2 - iPad Air',
        camera: 'Insta360 Pro 2',
        device: 'iPad Air',
        captureCount: 6,
        dateCreated: mockDaysAgo(now, 10),
        startedAt: mockDaysAgo(now, 10),
        drawingRevision: 3,
      },
      {
        id: '8',
        floorId: '9',
        name: 'Insta360 X3 - iPhone 14',
        camera: 'Insta360 X3',
        device: 'iPhone 14',
        captureCount: 4,
        dateCreated: mockDaysAgo(now, 2),
        startedAt: mockDaysAgo(now, 2),
        drawingRevision: 1,
      },
    ];
    this.sessions = mockSessions;
    this.idCounter = mockSessions.length;
  }

  getCaptureSessions(floorId: string): Observable<CaptureSession[]> {
    return of(this.sessions.filter((s) => s.floorId === floorId)).pipe(delay(100));
  }

  addCaptureSession(body: CreateCaptureSessionRequest): Observable<CaptureSession> {
    const now = new Date();
    const startedAt = body.startedAt ? new Date(body.startedAt) : now;
    const floor = this.floorsService.getFloorById(body.floorId);
    const session: CaptureSession = {
      id: String(++this.idCounter),
      floorId: body.floorId,
      name: body.captureSessionName,
      camera: body.camera,
      device: body.device,
      captureCount: 0,
      dateCreated: now,
      startedAt,
      drawingRevision: floor?.currentRevision ?? 0,
    };
    this.sessions.push(session);
    return of(session).pipe(delay(100));
  }

  incrementCaptureCount(sessionId: string): void {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (session) session.captureCount++;
  }

  getSessionById(sessionId: string): CaptureSession | undefined {
    return this.sessions.find((s) => s.id === sessionId);
  }
}
